import React, { useCallback, useState } from 'react';
import Dropzone from '../components/forms/form-elements/Dropzone';
import InputField from '../components/forms/form-elements/InputField';
import SwitchButton from '../components/buttons/SwitchButton';
import Divider from '../components/ui/Divider';
import Button from '../components/buttons/Button';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toBase64 } from '../utils/files';
import DescriptionInput from '../components/forms/form-elements/DescriptionInput';
import { toast } from 'react-toastify';
import { useComplexes } from '../hooks/useComplexes';
import { useTranslation } from 'react-i18next';
import { COMMON_TRANSLATE_KEYS } from '../constants/translate-keys';
import { getHeaders } from '../api/utils/getHeader';
import { TImage } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { editComplexSchema } from '../validation/schemas/complex';
import { updateSettings } from '../api/owner';
import { useResponsiveWindow } from '../hooks/useResponsiveWindow';

interface IFormInput {
  complexName: string;
  country: string;
  city: string;
  complexAddress: string;
  district?: string;
  phone: string;
  description?: string;
  latitude?: number;
  longitude?: number;
  hours: number;
}

const formatTime = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (!digits) return '';

  const hour = digits.slice(0, 2).padStart(2, '0');
  const minute = digits.slice(2).padEnd(2, '0');

  return `${hour}:${minute}`;
};

const isTimeInRange = (time: string) => {
  const [hours] = time.split(':').map(Number);
  return hours >= 1 && hours <= 23;
};

const CreateComplex = () => {
  const { t } = useTranslation();

  const { user, updateUser } = useAuth();

  const { isMobile } = useResponsiveWindow();

  const [submitting, setSubmitting] = useState(false);
  const [openingHours, setOpeningHours] = useState('');
  const [closingHours, setClosingHours] = useState('');
  const [parking, setParking] = useState(false);
  const [dressing, setDressing] = useState(false);
  const [showers, setShowers] = useState(false);
  const [coffee, setCoffee] = useState(false);
  const [providesBalls, setProvidesBalls] = useState(false);
  const [providesTShirts, setProvidesTShirts] = useState(false);
  const [openingHoursError, setOpeningHoursError] = useState<
    string | undefined
  >(undefined);
  const [closingHoursError, setClosingHoursError] = useState<
    string | undefined
  >(undefined);
  const [images, setImages] = useState<TImage[]>([]);

  const {
    register,
    setValue,
    setError,
    handleSubmit,
    reset: resetFormValues,
    formState: { errors, isValid },
  } = useForm<IFormInput>({
    defaultValues: {
      complexName: '',
      country: '',
      city: '',
      complexAddress: '',
      district: '',
      phone: '',
      description: '',
      latitude: 0,
      longitude: 0,
      hours: user?.cancelReservationHours || 12,
    },
    resolver: yupResolver(editComplexSchema),
    mode: 'all',
  });

  const navigate = useNavigate();

  const { getComplexes } = useComplexes();

  const addImages = useCallback((newImages: TImage[]) => {
    setImages((prev) => [...prev, ...newImages]);
  }, []);

  const removeImage = useCallback((image: TImage) => {
    setImages((prev) => prev.filter((i) => i.id !== image.id));
  }, []);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^[0-9]*$/;

    if (regex.test(value)) {
      setValue('phone', value);
      setError('phone', { message: undefined });
    } else {
      setError('phone', {
        message: 'Phone number can only contain numbers (0-9).',
      });
    }
  };

  const handleOpeningHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOpeningHours(e.target.value);
  };

  const handleClosingHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClosingHours(e.target.value);
  };

  const handleOpeningHoursBlur = () => {
    const formattedTime = formatTime(openingHours);
    setOpeningHours(formattedTime);
    setOpeningHoursError(validateOpeningHours(formattedTime));
  };

  const handleClosingHoursBlur = () => {
    const formattedTime = formatTime(closingHours);
    setClosingHours(formattedTime);
    setClosingHoursError(validateClosingHours(formattedTime));
  };

  const validateHours = (opening: string, closing: string) => {
    let error: string | undefined;

    if (!opening || !closing) {
      error = t(COMMON_TRANSLATE_KEYS.WORKING_HOURS_REQUIRED_ERROR);
    } else if (!/^\d{2}:\d{2}$/.test(opening)) {
      error = t(COMMON_TRANSLATE_KEYS.HOURS_ERROR);
    } else if (!isTimeInRange(opening)) {
      error = t(COMMON_TRANSLATE_KEYS.OPENING_HOURS_ERROR);
    } else if (!/^\d{2}:\d{2}$/.test(closing)) {
      error = t(COMMON_TRANSLATE_KEYS.HOURS_ERROR);
    } else if (!isTimeInRange(closing)) {
      error = t(COMMON_TRANSLATE_KEYS.CLOSING_HOURS_ERROR);
    } else if (opening >= closing) {
      error = t(COMMON_TRANSLATE_KEYS.OPEN_CLOSE_HOURS_ERROR);
    }

    return error;
  };

  const validateOpeningHours = (opening: string) => {
    let error: string | undefined;

    if (!/^\d{2}:\d{2}$/.test(opening)) {
      error = t(COMMON_TRANSLATE_KEYS.HOURS_ERROR);
    } else if (!isTimeInRange(opening)) {
      error = t(COMMON_TRANSLATE_KEYS.OPENING_HOURS_ERROR);
    }

    return error;
  };

  const validateClosingHours = (closing: string) => {
    let error: string | undefined;

    if (!/^\d{2}:\d{2}$/.test(closing)) {
      error = t(COMMON_TRANSLATE_KEYS.HOURS_ERROR);
    } else if (!isTimeInRange(closing)) {
      error = t(COMMON_TRANSLATE_KEYS.CLOSING_HOURS_ERROR);
    }

    return error;
  };

  const submitForm = async (formValues: IFormInput) => {
    setSubmitting(true);

    if (!isValid && Object.values(errors).length > 0) {
      toast.error(t(Object.values(errors)[0].message as string));
      setSubmitting(false);
      return;
    }

    const workingHoursError = validateHours(openingHours, closingHours);

    if (workingHoursError) {
      toast.error(workingHoursError);
      setSubmitting(false);
      return;
    }

    const base64Images = await Promise.all(
      images.map((img) => toBase64(img.file))
    ).then((res) => res.filter(Boolean));

    const { complexName, complexAddress, ...values } = formValues;

    const complexData = {
      ...values,
      name: complexName,
      address: complexAddress,
      parking,
      dressing,
      showers,
      coffee,
      providesBalls,
      providesTShirts,
      openingHours,
      closingHours,
      images: base64Images,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/owner/complexes`,
        complexData,
        {
          headers: getHeaders(true),
        }
      );

      const { data, error } = await updateSettings({ hours: values.hours }, t);

      if (error) {
        console.error('API Error: ', error.error);
        toast(error.error.message, {
          toastId: 'update-complex',
        });
        return;
      }

      if (data) {
        updateUser(data);
      }

      if (response.data) {
        toast.success(t(COMMON_TRANSLATE_KEYS.COMPLEX_CREATED));
        resetForm();
        await getComplexes();
        navigate('/my-fields');
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data;
        let errorMessage = t(COMMON_TRANSLATE_KEYS.UNEXPECTED_ERROR);

        if (errorResponse?.details) {
          const { msg, path } = errorResponse.details;

          if (path === 'openingHours' || path === 'closingHours') {
            errorMessage = `${msg}. ${t(
              COMMON_TRANSLATE_KEYS.INVALID_TIME_FORMAT_ERROR
            )}`;
          } else {
            errorMessage = msg || error.message;
          }
        } else {
          errorMessage = error.message;
        }

        toast.error(
          t(COMMON_TRANSLATE_KEYS.CREATE_COMPLEX_ERROR, { error: errorMessage })
        );
      } else {
        toast.error(t(COMMON_TRANSLATE_KEYS.UNEXPECTED_ERROR));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    resetFormValues();
    setOpeningHours('');
    setClosingHours('');
    setParking(false);
    setDressing(false);
    setShowers(false);
    setCoffee(false);
    setProvidesBalls(false);
    setProvidesTShirts(false);
    setImages([]);
    setOpeningHoursError(undefined);
    setClosingHoursError(undefined);
  };

  const handleCancel = () => {
    resetForm();
    navigate('/my-fields');
  };

  return (
    <>
      <div className="sm:block mx-auto sm:w-auto flex flex-col my-4 px-4 w-full items-center">
        <div className="text-mainWhite flex flex-col w-full">
          <p>{t(COMMON_TRANSLATE_KEYS.PHOTOS_OF_THE_COMPLEX)}</p>
          <Dropzone
            containerWidth={isMobile ? '100%' : '37rem'}
            addImages={addImages}
            removeImage={removeImage}
            images={images}
            showFullImageOnClick
          />
        </div>
        <div className="mt-4 w-full">
          <p className="text-mainWhite">
            {t(COMMON_TRANSLATE_KEYS.NAME_OF_THE_COMPLEX)}
            <span className="text-functionalRed">*</span>
          </p>
          <InputField
            placeholder={t(COMMON_TRANSLATE_KEYS.NAME)}
            error={t(errors.complexName?.message || '')}
            {...register('complexName')}
          />
        </div>
        <div className="sm:flex-row flex-col flex gap-x-4 w-full">
          <div className="mt-4 w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.COUNTRY)}
              <span className="text-functionalRed">*</span>
            </p>
            <InputField
              placeholder={t(COMMON_TRANSLATE_KEYS.COUNTRY)}
              error={t(errors.country?.message || '')}
              {...register('country')}
            />
          </div>
          <div className="mt-4 w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.REGION)}
              <span className="text-functionalRed">*</span>
            </p>
            <InputField
              placeholder={t(COMMON_TRANSLATE_KEYS.REGION)}
              error={t(errors.city?.message || '')}
              {...register('city')}
            />
          </div>
        </div>

        <div className="mt-4 w-full">
          <p className="text-mainWhite">
            {t(COMMON_TRANSLATE_KEYS.ADDRESS)}
            <span className="text-functionalRed">*</span>
          </p>
          <InputField
            placeholder={t(COMMON_TRANSLATE_KEYS.ADDRESS)}
            error={t(errors.complexAddress?.message || '')}
            {...register('complexAddress')}
          />
        </div>
        <div className="mt-4 w-full">
          <p className="text-mainWhite">{t(COMMON_TRANSLATE_KEYS.DISTRICT)}</p>
          <InputField
            placeholder={t(COMMON_TRANSLATE_KEYS.DISTRICT)}
            error={t(errors.district?.message || '')}
            {...register('district')}
          />
        </div>
        <div className="mt-4 w-full">
          <p className="text-mainWhite">
            {t(COMMON_TRANSLATE_KEYS.CONTACT_PHONE)}
            <span className="text-functionalRed">*</span>
          </p>

          <InputField
            placeholder={t(COMMON_TRANSLATE_KEYS.CONTACT_PHONE)}
            error={t(errors.phone?.message || '')}
            {...register('phone')}
            onChange={handlePhoneChange}
          />
        </div>
        <div className="mt-4 flex flex-row gap-4 w-full">
          <div className="w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.OPENING_HOURS)}
              <span className="text-functionalRed">*</span>
            </p>
            <InputField
              placeholder={t(COMMON_TRANSLATE_KEYS.OPENING_HOURS)}
              value={openingHours}
              onChange={handleOpeningHoursChange}
              onBlur={handleOpeningHoursBlur}
              width={18}
              error={openingHoursError}
            />
          </div>
          <div className="w-full">
            <p className="text-mainWhite">
              {t(COMMON_TRANSLATE_KEYS.CLOSING_HOURS)}
              <span className="text-functionalRed">*</span>
            </p>
            <InputField
              placeholder={t(COMMON_TRANSLATE_KEYS.CLOSING_HOURS)}
              value={closingHours}
              onChange={handleClosingHoursChange}
              onBlur={handleClosingHoursBlur}
              error={closingHoursError}
              width={18}
            />
          </div>
        </div>

        <div className="mt-4 mb-4 w-full">
          <p className="text-mainWhite">
            {t(COMMON_TRANSLATE_KEYS.ADDITIONAL_INFO)}
          </p>
          <DescriptionInput
            error={t(errors.description?.message || '')}
            {...register('description')}
            width="37rem"
            height={10}
          />
        </div>

        <div className="w-full flex sm:flex-col flex-row my-8 gap-4">
          <div className="flex sm:flex-row flex-col justify-around gap-2 sm:w-full w-1/2">
            <SwitchButton
              width={12}
              isSelected={parking}
              onClick={() => setParking(!parking)}
            >
              {t(COMMON_TRANSLATE_KEYS.PARKING)}
            </SwitchButton>
            <SwitchButton
              width={12}
              isSelected={dressing}
              onClick={() => setDressing(!dressing)}
            >
              {t(COMMON_TRANSLATE_KEYS.DRESSING_ROOM)}
            </SwitchButton>
            <SwitchButton
              width={12}
              isSelected={coffee}
              onClick={() => setCoffee(!coffee)}
            >
              {t(COMMON_TRANSLATE_KEYS.COFFEE_SHOP)}
            </SwitchButton>
          </div>
          <div className="flex sm:flex-row flex-col justify-around gap-2 sm:w-full w-1/2">
            <SwitchButton
              width={12}
              isSelected={showers}
              onClick={() => setShowers(!showers)}
            >
              {t(COMMON_TRANSLATE_KEYS.SHOWERS)}
            </SwitchButton>
            <SwitchButton
              width={12}
              isSelected={providesBalls}
              onClick={() => setProvidesBalls(!providesBalls)}
            >
              {t(COMMON_TRANSLATE_KEYS.PROVIDE_BALLS)}
            </SwitchButton>
            <SwitchButton
              width={12}
              isSelected={providesTShirts}
              onClick={() => setProvidesTShirts(!providesTShirts)}
            >
              {t(COMMON_TRANSLATE_KEYS.PROVIDE_T_SHIRTS)}
            </SwitchButton>
          </div>
        </div>

        <Divider mb={4} mt={8} />

        <div className="flex flex-col gap-5 w-full max-w-[600px] text-mainWhite">
          <p>{t(COMMON_TRANSLATE_KEYS.CANCEL_DEADLINE_TEXT)}</p>
          <div className="flex flex-row gap-6">
            <label className="mt-3">
              {t(COMMON_TRANSLATE_KEYS.HOURS, { count: 2 })}:
            </label>
            <div className="flex flex-row justify-between gap-2">
              <div className="flex flex-col">
                <InputField
                  placeholder={t(COMMON_TRANSLATE_KEYS.HOURS)}
                  {...register('hours')}
                  width={10}
                  height={3}
                />
                <div className="h-5 mt-2">
                  {errors.hours && (
                    <span className="text-red-500 text-sm">
                      {t(errors.hours.message || '')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Divider mb={2} mt={2} />

        <div className="flex sm:flex-row flex-col-reverse justify-around mt-8 w-full gap-4">
          <Button
            textColor="text-mainWhite"
            backgroundColor="bg-functionalRed"
            onClick={() => handleCancel()}
          >
            {t(COMMON_TRANSLATE_KEYS.CANCEL)}
          </Button>
          <Button
            textColor="text-black-500"
            backgroundColor="bg-functionalGreen"
            onClick={handleSubmit(submitForm)}
            isLoading={submitting}
            loadingText={`${t(COMMON_TRANSLATE_KEYS.CREATING)}...`}
          >
            {t(COMMON_TRANSLATE_KEYS.CREATE)}
          </Button>
        </div>
      </div>
    </>
  );
};

export default CreateComplex;
